import { MedplumClient } from "@medplum/core";
import { ChargeItem } from "@medplum/fhirtypes";

import { buildChargeItemStatusPatch } from "@/billing/components/ServicesTab/utils/charge-item-status-tag";
import { toFailureDetails } from "@/utils/errors";

import { SendEncounterServicesResult } from "../types/encounter-services-send";
import {
  CAPTURED_CHARGE_ITEM_STATUS,
  SENT_CHARGE_ITEM_STATUS,
  VOIDED_CHARGE_ITEM_STATUS,
} from "../utils/encounter-charge-items";

import { searchEncounterChargeItems } from "./encounter-charge-items-search";
import { tagSentServicesStatus } from "./encounter-services-status-tag";

/**
 * Moves one charge to `billable`.
 *
 * A patch on the status and its mirror tag, for the same reason voiding a charge is one: nothing
 * else on the charge, and nothing pointing at it, can be caught up in the change. A whole-resource
 * update would also put back whatever the read did not see. The mirror is what puts the charge
 * under the Services tab's Unbilled filter. See {@link buildChargeItemStatusPatch}.
 */
const sendChargeItem = (
  medplum: MedplumClient,
  chargeItemId: string,
  chargeItem: ChargeItem,
): Promise<ChargeItem> =>
  medplum.patchResource(
    "ChargeItem",
    chargeItemId,
    buildChargeItemStatusPatch(chargeItem, SENT_CHARGE_ITEM_STATUS),
  );

/**
 * Sends an encounter's Services to billing: every charge it captured moves from `planned` to
 * `billable`, and the encounter is then tagged `services-sent`.
 *
 * This is what the billing tab's `Send to Services` does. It is not a formality — `billable` is the
 * only status statement generation will put on a statement, so a charge left `planned` is invisible
 * to billing however complete the encounter is. See {@link SENT_CHARGE_ITEM_STATUS}.
 *
 * Voided charges are neither sent nor counted. Deleting a biller-section row is what voids one, and
 * an encounter is not billed for a line a biller took off it.
 *
 * Only `planned` charges are sent. Any other status is a decision someone already made about that
 * charge, so the send leaves it alone and still counts it as one of the encounter's Services. A
 * `not-billable` or `aborted` charge therefore does not hold the encounter back: `services-sent` says
 * nothing is left waiting to be sent, not that every charge is billable.
 *
 * The tag is written last and only when every charge moved, because the code says something exact:
 * `services-sent` means none of this encounter's Services are still waiting to go. A send that moved
 * some of them leaves the encounter reading `services-not-sent`, which is true, and keeps it in the
 * Missing Services queue where someone will see it. An encounter with no Services at all is not
 * tagged either — it has nothing to send, and claiming otherwise would drop it out of the queue
 * having never been billed.
 *
 * Safe to run twice, which is also how a failure is retried. Only `planned` charges are patched, so
 * a send that failed part-way moves just the remainder next time. A send that finds everything
 * already `billable` still re-asserts the tag, which is what repairs an encounter whose charges went
 * out but whose tag write did not.
 */
export const sendEncounterServices = async ({
  medplum,
  encounterId,
}: {
  medplum: MedplumClient;
  encounterId: string;
}): Promise<SendEncounterServicesResult> => {
  if (!encounterId.trim()) return { ok: false, reason: "missing_encounter_id" };

  let chargeItems: ChargeItem[];
  try {
    chargeItems = await searchEncounterChargeItems(medplum, encounterId);
  } catch (error) {
    return { ok: false, reason: "charges_read_failed", details: toFailureDetails(error) };
  }

  const services = chargeItems.filter(({ status }) => status !== VOIDED_CHARGE_ITEM_STATUS);

  // Nothing to send and nothing to claim. Returned before the tag so an encounter that was never
  // charged for anything keeps carrying no code from this system, which is its own state: the
  // Missing Services queue lists it under `No Services` rather than losing it.
  if (!services.length) return { ok: true, data: { sentCount: 0, serviceCount: 0 } };

  const unsent = services.filter(({ status }) => status === CAPTURED_CHARGE_ITEM_STATUS);
  const unsentWithIds = unsent.reduce<{ chargeItemId: string; chargeItem: ChargeItem }[]>(
    (sendable, chargeItem) =>
      chargeItem.id ? [...sendable, { chargeItemId: chargeItem.id, chargeItem }] : sendable,
    [],
  );

  // Checked before anything is patched rather than after. A charge the server returned without an id
  // cannot be sent at all, and discovering that once the tag was written would leave the encounter
  // claiming Services that never went.
  if (unsentWithIds.length !== unsent.length) {
    return { ok: false, reason: "send_failed", details: "A charge came back without an id" };
  }

  // The charges are independent of each other, so they all go out together. `allSettled` rather than
  // `all`, so one failure does not decide the fate of patches that were already in flight — every
  // charge that could move does, and the retry has less left to do.
  const outcomes = await Promise.allSettled(
    unsentWithIds.map(({ chargeItemId, chargeItem }) =>
      sendChargeItem(medplum, chargeItemId, chargeItem),
    ),
  );

  const failed = outcomes.find(
    (outcome): outcome is PromiseRejectedResult => outcome.status === "rejected",
  );

  if (failed) {
    return { ok: false, reason: "send_failed", details: toFailureDetails(failed.reason) };
  }

  // Written after the charges rather than with them, the same way capture writes its own code. See
  // tagServicesStatusAfterWrite for why the tag is a separate write and why failing it is not fatal,
  // and tagSentServicesStatus for how the tag is confirmed against a capture that ran meanwhile.
  return tagSentServicesStatus(medplum, encounterId, {
    sentCount: unsentWithIds.length,
    serviceCount: services.length,
  });
};
